/**
 * Client trigger for server-side inventory enrichment (enrich_my_inventory RPC
 * - the no-arg wrapper that always operates on the CALLER's rows; the
 * parameterized enrich_inventory is service/cron-only since the 2026-07-11
 * Phase 0 security audit).
 *
 * Fired after saves that can create lots WITHOUT catalog identity — purchases,
 * trades, adjustments (incl. Collectr CSV imports flowing through those
 * forms). The server links lots to catalog products (unique name+number
 * match), fills missing images, and reprices linked lots; we then pull the
 * enriched rows back into the local cache and re-render.
 *
 * Silent when there was nothing to do. The nightly cron runs the same
 * function for anything this misses.
 */
import { sb } from "./supabase";
import { DB } from "./db";
import { toast } from "./dom";

let _running = false;

export function requestEnrichment(): void {
  if (_running) return;
  _running = true;
  void (async () => {
    try {
      const { data, error } = await sb.rpc("enrich_my_inventory");
      if (error) throw error;
      const linked = (Number(data?.cards_linked) || 0) + (Number(data?.sealed_linked) || 0);
      const images = Number(data?.images_filled) || 0;
      const priced = (Number(data?.cards_updated) || 0) + (Number(data?.sealed_updated) || 0);
      if (linked + images + priced === 0) return;
      // Pull the enriched rows into the local cache, then refresh the view.
      await Promise.all([DB.refreshTable("cards"), DB.refreshTable("sealed")]);
      (window as any).render?.();
      toast(`Auto-matched: ${linked} linked · ${images} image(s) · ${priced} repriced`);
      // Copy any newly-referenced external images into our own storage.
      if (images > 0 || linked > 0) {
        void sb.functions.invoke("cache-card-image", { body: { mode: "batch" } }).catch(() => { /* best effort */ });
      }
    } catch (e) {
      console.warn("[enrich]", e);
    } finally {
      _running = false;
    }
  })();
}
